'use client';

import type { ScheduledReportRule } from '@terra/shared-contracts-applications';
import { Box, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { automationId } from '@terra/shared-utils';
import { Select, createSelectListCollection } from '../../../components/select';
import { MetaDefault } from '../../../components/typography';
import { SettingsRow } from '../SettingsRow';
import { WeeklyDaysControl } from './WeeklyDaysControl';
import {
  DAY_OF_MONTH_OPTIONS,
  MONTH_OPTIONS,
  clampYearlyDayToMonth,
  getYearlyDayOptions,
  type ScheduledReportRuleFieldErrors,
} from './scheduled-report-rule.utils';

type ScheduledReportFrequencyFieldsProps = {
  rule: ScheduledReportRule;
  disabled?: boolean;
  fieldErrors: ScheduledReportRuleFieldErrors;
  onChange: (rule: ScheduledReportRule) => void;
  monthlyDaySelectAutomationId?: string;
};

const MONTH_LABEL_KEYS = [
  'months.1',
  'months.2',
  'months.3',
  'months.4',
  'months.5',
  'months.6',
  'months.7',
  'months.8',
  'months.9',
  'months.10',
  'months.11',
  'months.12',
] as const;

export const ScheduledReportFrequencyFields = ({
  rule,
  disabled,
  fieldErrors,
  onChange,
  monthlyDaySelectAutomationId,
}: ScheduledReportFrequencyFieldsProps) => {
  const t = useTranslations('designSystem.automation.scheduledReports');
  const dayOfMonthItems = useMemo(
    () => [
      ...DAY_OF_MONTH_OPTIONS.map((day) => ({
        value: String(day),
        label: String(day),
      })),
      { value: 'last', label: t('lastDayOfMonth') },
    ],
    [t],
  );
  const dayOfMonthCollection = useMemo(
    () => createSelectListCollection({ items: dayOfMonthItems }),
    [dayOfMonthItems],
  );
  const monthItems = useMemo(
    () =>
      MONTH_OPTIONS.map((month, index) => ({
        value: String(month),
        label: t(MONTH_LABEL_KEYS[index] ?? 'months.1'),
      })),
    [t],
  );
  const monthCollection = useMemo(
    () => createSelectListCollection({ items: monthItems }),
    [monthItems],
  );
  const yearDayItems = useMemo(
    () =>
      getYearlyDayOptions(rule.yearlyMonth).map((day) => ({
        value: String(day),
        label: String(day),
      })),
    [rule.yearlyMonth],
  );
  const yearDayCollection = useMemo(
    () => createSelectListCollection({ items: yearDayItems }),
    [yearDayItems],
  );
  const dayOfMonthValue = rule.lastDayOfMonth
    ? 'last'
    : rule.dayOfMonth !== null && rule.dayOfMonth !== undefined
      ? String(rule.dayOfMonth)
      : '';

  if (rule.frequency === 'WEEKLY') {
    return (
      <VStack alignItems="stretch" gap={1} w="full">
        <SettingsRow
          label={t('dayOfWeek')}
          control={
            <WeeklyDaysControl
              daysOfWeek={rule.daysOfWeek}
              disabled={disabled}
              onSelectDay={(day) => onChange({ ...rule, daysOfWeek: [day] })}
            />
          }
        />
        {fieldErrors.daysOfWeek ? (
          <MetaDefault color="fg.error">{t('dayOfWeekRequired')}</MetaDefault>
        ) : null}
      </VStack>
    );
  }

  if (rule.frequency === 'MONTHLY') {
    return (
      <VStack alignItems="stretch" gap={1} w="full">
        <SettingsRow
          label={t('dayOfMonth')}
          control={
            <Box
              minW="120px"
              {...(monthlyDaySelectAutomationId
                ? automationId(monthlyDaySelectAutomationId)
                : {})}
            >
              <Select.Root
                collection={dayOfMonthCollection}
                positioning={{ sameWidth: true }}
                value={dayOfMonthValue ? [dayOfMonthValue] : []}
                disabled={disabled}
                onValueChange={(details) => {
                  const value = details.value[0];

                  if (!value) {
                    return;
                  }

                  if (value === 'last') {
                    onChange({
                      ...rule,
                      dayOfMonth: null,
                      lastDayOfMonth: true,
                    });

                    return;
                  }

                  onChange({
                    ...rule,
                    dayOfMonth: Number.parseInt(value, 10),
                    lastDayOfMonth: false,
                  });
                }}
                size="sm"
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('selectDayOfMonth')} />
                    <ArrowDown01Icon size={14} />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup maxH="240px" overflowY="auto">
                      {dayOfMonthItems.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={16} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Box>
          }
        />
        {fieldErrors.dayOfMonth ? (
          <MetaDefault color="fg.error">{t('dayOfMonthRequired')}</MetaDefault>
        ) : null}
      </VStack>
    );
  }

  if (rule.frequency === 'QUARTERLY' || rule.frequency === 'SEMI_ANNUAL') {
    const startMonthLabelKey =
      rule.frequency === 'SEMI_ANNUAL'
        ? 'startingMonth'
        : 'fiscalYearStartMonth';
    const startMonthRequiredKey =
      rule.frequency === 'SEMI_ANNUAL'
        ? 'startingMonthRequired'
        : 'fiscalYearStartMonthRequired';

    return (
      <VStack alignItems="stretch" gap={1} w="full">
        <SettingsRow
          label={t(startMonthLabelKey)}
          control={
            <Box minW="160px">
              <Select.Root
                collection={monthCollection}
                positioning={{ sameWidth: true }}
                value={
                  rule.fiscalYearStartMonth !== null &&
                  rule.fiscalYearStartMonth !== undefined
                    ? [String(rule.fiscalYearStartMonth)]
                    : []
                }
                disabled={disabled}
                onValueChange={(details) => {
                  const value = details.value[0];

                  if (!value) {
                    return;
                  }

                  onChange({
                    ...rule,
                    fiscalYearStartMonth: Number.parseInt(value, 10),
                  });
                }}
                size="sm"
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('selectMonth')} />
                    <ArrowDown01Icon size={14} />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup maxH="240px" overflowY="auto">
                      {monthItems.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={16} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Box>
          }
        />
        {fieldErrors.fiscalYearStartMonth ? (
          <MetaDefault color="fg.error">{t(startMonthRequiredKey)}</MetaDefault>
        ) : null}
      </VStack>
    );
  }

  if (rule.frequency === 'YEARLY') {
    return (
      <VStack alignItems="stretch" gap={1} w="full">
        <SettingsRow
          label={t('yearlyMonth')}
          control={
            <Box minW="160px">
              <Select.Root
                collection={monthCollection}
                positioning={{ sameWidth: true }}
                value={
                  rule.yearlyMonth !== null && rule.yearlyMonth !== undefined
                    ? [String(rule.yearlyMonth)]
                    : []
                }
                disabled={disabled}
                onValueChange={(details) => {
                  const value = details.value[0];

                  if (!value) {
                    return;
                  }

                  const yearlyMonth = Number.parseInt(value, 10);

                  onChange({
                    ...rule,
                    yearlyMonth,
                    yearlyDay: clampYearlyDayToMonth({
                      yearlyMonth,
                      yearlyDay: rule.yearlyDay,
                    }),
                  });
                }}
                size="sm"
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('selectMonth')} />
                    <ArrowDown01Icon size={14} />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup maxH="240px" overflowY="auto">
                      {monthItems.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={16} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Box>
          }
        />
        {fieldErrors.yearlyMonth ? (
          <MetaDefault color="fg.error">{t('yearlyMonthRequired')}</MetaDefault>
        ) : null}
        <SettingsRow
          label={t('yearlyDay')}
          control={
            <Box minW="120px">
              <Select.Root
                collection={yearDayCollection}
                positioning={{ sameWidth: true }}
                value={
                  rule.yearlyDay !== null && rule.yearlyDay !== undefined
                    ? [String(rule.yearlyDay)]
                    : []
                }
                disabled={disabled}
                onValueChange={(details) => {
                  const value = details.value[0];

                  if (!value) {
                    return;
                  }

                  onChange({
                    ...rule,
                    yearlyDay: Number.parseInt(value, 10),
                  });
                }}
                size="sm"
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('selectDay')} />
                    <ArrowDown01Icon size={14} />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup maxH="240px" overflowY="auto">
                      {yearDayItems.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={16} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Box>
          }
        />
        {fieldErrors.yearlyDay ? (
          <MetaDefault color="fg.error">{t('yearlyDayRequired')}</MetaDefault>
        ) : null}
      </VStack>
    );
  }

  return null;
};
